import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { PaymentMethod, Payment } from '@ibe/models';
import { INDRATotalFlightPrice } from '@ibe.models/flight-price.model.indra.lib';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

	/** All available payment methods */
	public paymentMethods: any = {
		credit_card: {
			id: 'credit_card',
			image: 'credit_card',
			label: 'Credit card',
			// details: '5 EUR surcharge'
		},
		debit_card: {
			id: 'debit_card',
			image: 'debit_card',
			label: 'Debit card'
		},
		multibanco: {
			id: 'multibanco',
			image: 'multibanco',
			label: 'Multibanco'
		},
		mb_way: {
			id: 'mb_way',
			image: 'mb_way',
			label: 'MB Way'
		},
		paypal: {
			id: 'paypal',
			image: 'paypal',
			label: 'Paypal'
		},
		alipay: {
			id: 'alipay',
			image: 'alipay',
			label: 'Alipay'
		},
		bank_transfer: {
			id: 'bank_transfer',
			image: 'bank_transfer',
			label: 'Bank Transfer'
		},
		pickup: {
			id: 'pickup',
			image: 'pickup',
			label: 'Pickup'
		},
		klarna_pay_now: {
			id: 'klarna_pay_now',
			image: 'klarna',
			label: 'Klarna Pay Now'
		},
		klarna_pay_later: {
			id: 'klarna_pay_later',
			image: 'klarna',
			label: 'Klarna Pay Later'
		},
		bancontact: {
			id: 'bancontact',
			image: 'bancontact',
			label: 'Bancontact'
		},
		ideal: {
			id: 'ideal',
			image: 'ideal',
			label: 'Ideal'
		},
		multicaixa: {
			id: 'multicaixa',
			image: 'multicaixa',
			label: 'Multicaixa'
		},
		poli: {
			id: 'poli',
			image: 'poli',
			label: 'POLi'
		},
		caixa_loterias: {
			id: 'caixa_loterias',
			image: 'caixa_loterias',
			label: 'Loterias'
		},
		bank_transfer_br: {
			id: 'bank_transfer_br',
			image: 'bank_transfer',
			label: 'Transferência bancária'
		}
	};
	
	public cardWithoutCVC = [
		'uatp',
		'bancontact'
	];
	
	public asynchronousPaymentMethods = [
		'multibanco',
		'mb_way',
		// 'sofort',
		// 'ideal',
		// 'poli',
		'pickup',
		'multicaixa',
		'bank_transfer'
	];

	private _formIsValid: boolean;
	formIsValid: BehaviorSubject<boolean>;
	
	private _paymentFormSubmitted: string;
	paymentFormSubmitted: BehaviorSubject<string>;

	private _triedToSubmit: boolean;
	triedToSubmit: BehaviorSubject<boolean>;

	private _selectedMethod: PaymentMethod;
	selectedMethod: BehaviorSubject<PaymentMethod>;
	
	private _selectedCardType: string;
	selectedCardType: BehaviorSubject<string>;
	
	private _savePaypalData: boolean;
	savePaypalData: BehaviorSubject<boolean>;
	
	private _saveCardData: boolean;
	saveCardData: BehaviorSubject<boolean>;
	
	private _paypalPaymentResult: string;
	paypalPaymentResult: BehaviorSubject<string>;
	
	private _prpPaymentResult: string;
	prpPaymentResult: BehaviorSubject<string>;
	
	private _genericPaymentResult: string;
	genericPaymentResult: BehaviorSubject<string>;

	private _paymentInfo: Payment;
	private _idOperation: string;
	
	private _clientIPAdrress: string;
	clientIPAddress: BehaviorSubject<string>;
	
	private _surcharge: number = 0;
	surcharge: BehaviorSubject<number>;
	
	private _surchargeAmount: number = 0;
	surchargeAmount: BehaviorSubject<number>;
	
	private _installmentAmount: number = 0;
	installmentAmount: BehaviorSubject<number>;
	
	private _totalFlightPrice: INDRATotalFlightPrice;
	totalFlightPrice: BehaviorSubject<INDRATotalFlightPrice>;
	
	private _clearPaymentForm: boolean;
	clearPaymentForm: BehaviorSubject<boolean>;
	
	private _updateBookingTotals: boolean;
	updateBookingTotals: BehaviorSubject<boolean>;
	
	private _triggerCheckPaymentMethods: boolean;
	triggerCheckPaymentMethods: BehaviorSubject<boolean>;
	
	_paymentTryDecement = new Subject;
	paymentTryDecrement = this._paymentTryDecement.asObservable();

  constructor() {
		this.formIsValid = new BehaviorSubject(this._formIsValid);
		this.paymentFormSubmitted = new BehaviorSubject(this._paymentFormSubmitted);
		this.selectedMethod = new BehaviorSubject(this._selectedMethod);
		this.triedToSubmit = new BehaviorSubject(this._triedToSubmit);
		this.selectedCardType = new BehaviorSubject(this._selectedCardType);
		this.saveCardData = new BehaviorSubject(this._saveCardData);
		this.savePaypalData = new BehaviorSubject(this._savePaypalData);
		this.paypalPaymentResult = new BehaviorSubject(this._paypalPaymentResult);
		this.prpPaymentResult = new BehaviorSubject(this._prpPaymentResult);
		this.genericPaymentResult = new BehaviorSubject(this._genericPaymentResult);
		this.clientIPAddress = new BehaviorSubject(this._clientIPAdrress);
		this.surcharge = new BehaviorSubject(this._surcharge);
		this.surchargeAmount = new BehaviorSubject(this._surchargeAmount);
		this.installmentAmount = new BehaviorSubject(this._installmentAmount);
		this.totalFlightPrice = new BehaviorSubject(this._totalFlightPrice);
		this.clearPaymentForm = new BehaviorSubject(this._clearPaymentForm);
		this.updateBookingTotals = new BehaviorSubject(this._updateBookingTotals);
		this.triggerCheckPaymentMethods = new BehaviorSubject(this._triggerCheckPaymentMethods);
		this._idOperation = null;
	}

	/** Reset payment */
	resetPayment(excludeTotalPrice = false) {
		this._paymentInfo = null;
		this._idOperation = null;
		
		this._formIsValid = false;
		this.formIsValid.next(this._formIsValid);
		
		this._paymentFormSubmitted = null;
		this.paymentFormSubmitted.next(this._paymentFormSubmitted);

		this._triedToSubmit = false;
		this.triedToSubmit.next(this._triedToSubmit);

		this._selectedMethod = undefined;
		this.selectedMethod.next(this._selectedMethod);
		
		this._selectedCardType = undefined;
		this.selectedCardType.next(this._selectedCardType);
		
		this._saveCardData = false;
		this.saveCardData.next(this._saveCardData);
		
		this._savePaypalData = false;
		this.savePaypalData.next(this._savePaypalData);
		
		this._paypalPaymentResult = null;
		this.paypalPaymentResult.next(this._paypalPaymentResult);
		
		this._prpPaymentResult = null;
		this.prpPaymentResult.next(this._prpPaymentResult);
		
		this._genericPaymentResult = null;
		this.genericPaymentResult.next(this._genericPaymentResult);
		
		this._clientIPAdrress = null;
		this.clientIPAddress.next(this._clientIPAdrress);
		
		this._surcharge = 0;
		this.surcharge.next(this._surcharge);
		
		this._surchargeAmount = 0;
		this.surchargeAmount.next(this._surchargeAmount);
		
		this._installmentAmount = 0;
		this.installmentAmount.next(this._installmentAmount);
		
		this._clearPaymentForm = false;
		this.clearPaymentForm.next(this._clearPaymentForm);
		
		this._updateBookingTotals = false;
		this.updateBookingTotals.next(this._updateBookingTotals);
		
		this._triggerCheckPaymentMethods = false;
		this.triggerCheckPaymentMethods.next(this._triggerCheckPaymentMethods);
		
		if (!excludeTotalPrice) {
			this._totalFlightPrice = undefined;
			this.totalFlightPrice.next(this._totalFlightPrice);
		}
	}

	/** Set if payment form is valid */
	setFormIsValid(valid: boolean) {
		this._formIsValid = valid;
		this.formIsValid.next(this._formIsValid);
	}

	/** Get if payment form is valid */
	getFormIsValid(): boolean {
		return this._formIsValid;
	}

	/** Set if payment form was submited */
	setTriedToSubmit(valid: boolean) {
		this._triedToSubmit = valid;
		this.triedToSubmit.next(this._triedToSubmit);
	}

	/** Get if payment form was submited */
	getTriedToSubmit(): boolean {
		return this._triedToSubmit;
	}

	/** Set payment method */
	setSelectedMethod(method: PaymentMethod) {
		this._selectedMethod = method;
		this.selectedMethod.next(this._selectedMethod);
	}

	/** Set payment info */
	setPaymentInfo(paymentInfo: Payment) {
		this._paymentInfo = paymentInfo;
	}

	/** Get payment info */
	getPaymentInfo():Payment {
		return this._paymentInfo;
	}
	
	/** Set payment operation id */
	setPaymentOperationID(operationID: string) {
		this._idOperation = operationID;
	}

	/** payment operation id */
	getPaymentOperationID(): string {
		return this._idOperation;
	}

	/** Get payment method */
	getSelectedMethod(): PaymentMethod {
		return this._selectedMethod;
	}
	
	/** Set which payment form is submitted */
	setPaymentFormSubmitted(paymentFormSubmitted: string) {
		this._paymentFormSubmitted = paymentFormSubmitted;
		this.paymentFormSubmitted.next(this._paymentFormSubmitted);
	}
	
	/** Get which payment form is submitted  */
	getPaymentFormSubmitted(): string {
		return this._paymentFormSubmitted;
	}
	
	/** Set card type (available, expired, other) */
	setSelectedCardType(cardType: string) {
		this._selectedCardType = cardType;
		this.selectedCardType.next(this._selectedCardType);
	}
	
	/** Get payment method */
	getSelectedCardType(): string {
		return this._selectedCardType;
	}

	/** Submit payment */
	submitPayment() {
		// this.setTriedToSubmit(true);
		this.setPaymentFormSubmitted(this.getSelectedMethod());
	}
	
	/** Set save card data */
	setSaveCardData(save: boolean) {
		this._saveCardData = save;
		this.saveCardData.next(this._saveCardData);
	}
	
	/** Get save card data (checkbox) */
	getSaveCardData(): boolean {
		return this._saveCardData;
	}
	
	/** Set save paypal data checkbox value */
	setPaypalCheck(valid: boolean) {
		this._savePaypalData = valid;
		this.savePaypalData.next(this._savePaypalData);
	}
	
	/** Get save paypal data checkbox value  */
	getPaypalCheck(): boolean {
		return this._savePaypalData;
	}
	
	/** Set paypal payment result */
	setPaypalPaymentResult(result: string) {
		this._paypalPaymentResult = result;
		this.paypalPaymentResult.next(this._paypalPaymentResult);
	}
	
	/** Get paypal payment result  */
	getPaypalPaymentResult(): string {
		return this._paypalPaymentResult;
	}
	
	/** Set PRP payment result */
	setPRPPaymentResult(result: string) {
		this._prpPaymentResult = result;
		this.prpPaymentResult.next(this._prpPaymentResult);
	}
	
	/** Get PRP payment result  */
	getPRPPaymentResult(): string {
		return this._prpPaymentResult;
	}
	
	/** Set generic payment result */
	setGenericPaymentResult(result: string) {
		this._genericPaymentResult = result;
		this.genericPaymentResult.next(this._genericPaymentResult);
	}
	
	/** Get generic payment result  */
	getGenericPaymentResult(): string {
		return this._genericPaymentResult;
	}
	
	/** Set client IP address */
	setClientIPAddress(clientIPAddress: string) {
		this._clientIPAdrress = clientIPAddress;
		this.clientIPAddress.next(this._clientIPAdrress);
	}
	
	/** Get client IP address  */
	getClientIPAddress(): string {
		return this._clientIPAdrress;
	}
	
	/** Set surcharge percentage */
	setSurcharge(surcharge: number) {
		this._surcharge = surcharge;
		this.surcharge.next(this._surcharge);
	}
	
	/** Get surcharge  percentage */
	getSurcharge(): number {
		return this._surcharge;
	}
	
	/** Set surcharge amount */
	setSurchargeAmount(surchargeAmount: number) {
		this._surchargeAmount = surchargeAmount;
		this.surchargeAmount.next(this._surchargeAmount);
	}
	
	/** Get surcharge amount  */
	getSurchargeAmount(): number {
		return this._surchargeAmount;
	}
	
	/** Set installment amount */
	setInstallmentAmount(installmentAmount: number) {
		this._installmentAmount = installmentAmount;
		this.installmentAmount.next(this._installmentAmount);
	}
	
	/** Get installment amount  */
	getInstallmentAmount(): number {
		return this._installmentAmount;
	}
	
	/** Set total flight price */
	setTotalFlightPrice(totalFlightPrice: INDRATotalFlightPrice) {
		this._totalFlightPrice = totalFlightPrice;
		this.totalFlightPrice.next(this._totalFlightPrice);
	}
	
	/** Get total flight price */
	getTotalFlightPrice(): INDRATotalFlightPrice {
		return this._totalFlightPrice;
	}
	
	/** Set clear payment form value */
	setClearPaymentForm(clear: boolean) {
		this._clearPaymentForm = clear;
		this.clearPaymentForm.next(this._clearPaymentForm);
	}
	
	/** Get clear payment form value */
	getClearPaymentForm(): boolean {
		return this._clearPaymentForm;
	}
	
	/** Set update booking totals form value */
	setUpdateBookingTotals(update: boolean) {
		this._updateBookingTotals = update;
		this.updateBookingTotals.next(this._updateBookingTotals);
	}
	
	/** Get update booking totals form value */
	getUpdateBookingTotals(): boolean {
		return this._updateBookingTotals;
	}

	/** Set trigger checkPaymentMethods */
	setTriggerCheckPaymentMethods(update: boolean) {
		this._triggerCheckPaymentMethods = update;
		this.triggerCheckPaymentMethods.next(this._triggerCheckPaymentMethods);
	}
	
	/** Get trigger checkPaymentMethods */
	getTriggerCheckPaymentMethods(): boolean {
		return this._triggerCheckPaymentMethods;
	}

	INDRA_decrementPaymentTry(){
		this._paymentTryDecement.next(true);
	}
}
